import React from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Settings,
  FileText,
  Search,
  Bell,
  User,
  ArrowLeft,
  Menu,
  ClipboardList,
  Droplet,
  ToggleLeft,
  Activity,
  Link,
  Send
} from 'lucide-react';

// Import existing visuals
import ChatWidgetVisual from './visuals/ChatWidgetVisual';
import LeadGenVisual from './visuals/LeadGenVisual';
import TranscriptsVisual from './visuals/TranscriptsVisual';
import AnalyticsVisual from './visuals/AnalyticsVisual';
import CustomizationVisual from './visuals/CustomizationVisual';
import MultiBrandVisual from './visuals/MultiBrandVisual';
import FilesVisual from './visuals/FilesVisual';
import WooCommerceVisual from './visuals/WooCommerceVisual';

interface DashboardShellProps {
  activeFeature: string;
}

const ShellContainer = styled.div`
  width: 100%;
  height: 100%;
  background: #f3f4f6;
  display: flex;
  overflow: hidden;
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: 1px solid #e5e7eb;
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const Sidebar = styled.div`
  width: 240px;
  background: #ffffff;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  
  @media (max-width: 1024px) {
    width: 60px;
  }
`;

const SidebarHeader = styled.div`
  height: 60px;
  display: flex;
  align-items: center;
  padding: 0 20px;
  border-bottom: 1px solid #f3f4f6;
  gap: 12px;
  
  @media (max-width: 1024px) {
    padding: 0;
    justify-content: center;
  }
`;

const Logo = styled.div`
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #7c3aed, #6d28d9);
  border-radius: 6px;
  flex-shrink: 0;
`;

const BrandName = styled.span`
  font-weight: 700;
  font-size: 16px;
  color: #111827;
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const Nav = styled.div`
  flex: 1;
  padding: 20px 12px;
  overflow-y: auto;
`;

const NavGroup = styled.div`
  margin-bottom: 16px;
`;

const GroupLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
  padding: 0 12px;
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const NavItem = styled.div<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px;
  border-radius: 8px;
  color: ${props => props.active ? '#7c3aed' : '#4b5563'};
  background: ${props => props.active ? '#f5f3ff' : 'transparent'};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f9fafb;
    color: #111827;
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
  
  @media (max-width: 1024px) {
    justify-content: center;
    padding: 10px;
    
    span {
      display: none;
    }
  }
`;

const BackItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 20px;
  
  &:hover {
    color: #111827;
  }
`;

const MainArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #f9fafb;
  overflow: hidden;
`;

const TopBar = styled.div`
  height: 60px;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
`;

const Breadcrumb = styled.div`
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
`;

const TopRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const IconButton = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  cursor: pointer;
  
  &:hover {
    color: #111827;
  }
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid #d1d5db;
`;

const ContentContainer = styled.div`
  flex: 1;
  // padding: 24px;
  overflow: hidden;
  position: relative;
`;

const VisualWrapper = styled(motion.div)`
  width: 100%;
  height: 100%;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;

const DashboardShell: React.FC<DashboardShellProps> = ({ activeFeature }) => {

  // Map activeFeature to a "Page Title" and "Nav Highlight"
  const getPageInfo = (feature: string) => {
    switch (feature) {
      case 'chat-widget': return { title: 'Customize / Profile', nav: 'profile' };
      case 'lead-gen': return { title: 'Knowledge Base / Forms', nav: 'forms' };
      case 'transcripts': return { title: 'Analytics / Transcripts', nav: 'transcripts' };
      case 'analytics': return { title: 'Analytics / Dashboard', nav: 'analytics' };
      case 'customization': return { title: 'Customize / Theme', nav: 'theme' };
      case 'multi-brand': return { title: 'Chatbots', nav: 'chatbots' }; // Highlights back link
      case 'files': return { title: 'Knowledge Base / Files', nav: 'files' };
      case 'woocommerce': return { title: 'Integrations / Integration', nav: 'integrations' };
      default: return { title: 'Dashboard', nav: 'dashboard' };
    }
  };

  const { title, nav } = getPageInfo(activeFeature);

  const renderVisual = () => {
    switch (activeFeature) {
      case 'chat-widget': return <ChatWidgetVisual />;
      case 'lead-gen': return <LeadGenVisual />;
      case 'transcripts': return <TranscriptsVisual />;
      case 'analytics': return <AnalyticsVisual />;
      case 'customization': return <CustomizationVisual />;
      case 'multi-brand': return <MultiBrandVisual />;
      case 'files': return <FilesVisual />;
      case 'woocommerce': return <WooCommerceVisual />;
      default: return <ChatWidgetVisual />;
    }
  };

  return (
    <ShellContainer>
      <Sidebar>
        <SidebarHeader>
          <Logo />
          <BrandName>SmushLabs</BrandName>
        </SidebarHeader>
        <Nav>
          <BackItem>
            <ArrowLeft size={18} />
            <span>Chatbots</span>
          </BackItem>

          <NavGroup>
            <GroupLabel>Knowledge Base</GroupLabel>
            <NavItem active={nav === 'files'}><FileText /> <span>Files</span></NavItem>
            <NavItem><Menu /> <span>Chat Menu</span></NavItem>
            <NavItem active={nav === 'forms'}><ClipboardList /> <span>Forms</span></NavItem>
          </NavGroup>

          <NavGroup>
            <GroupLabel>Customize</GroupLabel>
            <NavItem active={nav === 'profile'}><User /> <span>Profile</span></NavItem>
            <NavItem active={nav === 'theme'}><Droplet /> <span>Theme</span></NavItem>
            <NavItem><ToggleLeft /> <span>Toggle Button</span></NavItem>
            <NavItem><Settings /> <span>Advance</span></NavItem>
          </NavGroup>

          <NavGroup>
            <GroupLabel>Analytics</GroupLabel>
            <NavItem active={nav === 'analytics'}><Activity /> <span>Dashboard</span></NavItem>
            <NavItem active={nav === 'transcripts'}><FileText /> <span>Transcripts</span></NavItem>
          </NavGroup>

          <NavGroup>
            <GroupLabel>Integrations</GroupLabel>
            <NavItem active={nav === 'integrations'}><Link /> <span>Integration</span></NavItem>
          </NavGroup>

          <NavGroup>
            <GroupLabel>Deploy</GroupLabel>
            <NavItem><Send /> <span>Publish</span></NavItem>
          </NavGroup>
        </Nav>
      </Sidebar>

      <MainArea>
        <TopBar>
          <Breadcrumb>{title}</Breadcrumb>
          <TopRight>
            <IconButton><Search size={18} /></IconButton>
            <IconButton><Bell size={18} /></IconButton>
            <UserAvatar>
              <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Felix" alt="User" />
            </UserAvatar>
          </TopRight>
        </TopBar>

        <ContentContainer>
          <AnimatePresence mode="wait">
            <VisualWrapper
              key={activeFeature}
              initial={{ opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.98 }}
              transition={{ duration: 0.3 }}
            >
              {renderVisual()}
            </VisualWrapper>
          </AnimatePresence>
        </ContentContainer>
      </MainArea>
    </ShellContainer >
  );
};

export default DashboardShell;
